/**
 * Size Guide Web Component
 *
 * This web component creates a modal drawer to display size guide information for a product.
 * It handles the rendering of size guide data, including title, description, image, and table,
 * in a reusable drawer that can be triggered from any product card or page.
 *
 * Features:
 * - Renders a shared overlay and drawer for all size guides
 * - Dynamically updates drawer content based on the clicked button's data
 * - Uses an SVG icon for the close button
 * - Supports smooth scrolling within the drawer
 * - Disables body scrolling while the drawer is active
 * - Closes the drawer on ESC key press
 * - Automatically focuses the drawer wrapper when the drawer opens
 * - Supports opening the drawer via a link in the product description
 *
 * @class
 * @extends HTMLElement
 */
class SizeGuide extends HTMLElement {
  /**
   * Shared overlay element that darkens the background behind the drawer
   * @static
   * @type {HTMLElement|null}
   */
  static overlay = null;

  /**
   * Shared drawer element that contains the size guide content
   * @static
   * @type {HTMLElement|null}
   */
  static drawer = null;

  /**
   * SVG markup for the close icon used in the drawer header
   * @static
   * @type {string}
   */
  static closeIconSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M6 18L18 6M6 6L18 18" stroke="#191919" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  /**
   * Creates an instance of SizeGuide component and initializes the drawer
   * @constructor
   */
  constructor() {
    super();
  }

  connectedCallback() {
    this.sizeGuideButton = this.querySelector('.size-guide-button');
    this.setupDrawer();
  }

  /**
   * Initializes the component and sets up the drawer elements
   *
   * This method is called from the constructor and:
   * - Finds the size guide button within the component
   * - Stores a reference to the button for focus management
   * - Creates shared overlay and drawer elements if they don't exist
   * - Sets up event listeners for button and link clicks
   */
  setupDrawer() {
    const button = this.sizeGuideButton || document.querySelector('.size-guide-button');
    if (!button) return;

    // Store the button for focus management
    this.openButton = button;

    this.initializeOverlayAndDrawer();

    // Handle clicks on the size-guide-button
    button.addEventListener('click', (event) => {
      this.lastTriggerElement = event.currentTarget;
      this.handleButtonClick();
    });

    // Handle clicks on size-guide-opener-link elements in the product description
    const openerLinks = document.querySelectorAll('.size-guide-opener-link');
    openerLinks.forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.lastTriggerElement = event.currentTarget;
        this.handleLinkClick();
      });
    });
  }

  /**
   * Creates or reuses the overlay and drawer elements
   *
   * This method ensures that only one overlay and drawer exist in the DOM,
   * which are shared across all instances of the component.
   */
  initializeOverlayAndDrawer() {
    if (!SizeGuide.overlay) {
      SizeGuide.overlay = document.createElement('div');
      SizeGuide.overlay.classList.add('size-guide-overlay');
      document.body.appendChild(SizeGuide.overlay);
    }

    if (!SizeGuide.drawer) {
      SizeGuide.drawer = document.createElement('div');
      SizeGuide.drawer.classList.add('size-guide-drawer');
      document.body.appendChild(SizeGuide.drawer);
    }
  }

  /**
   * Handles button click events to open the size guide drawer
   *
   * This method:
   * - Extracts size guide data from the button's data attribute
   * - Parses the JSON data to get title, description, image, etc.
   * - Generates HTML table content if table data is present
   * - Updates the drawer content and opens the drawer
   */
  handleButtonClick() {
    const sizeGuideData = this.sizeGuideButton.getAttribute('data-size-guide') || '{}';
    const { title, description, image, table, pdf, print_button_text } = JSON.parse(sizeGuideData);

    const tableHTML = this.generateTableHTML(table);
    this.updateDrawerContent(title, description, image, tableHTML, pdf, print_button_text);
    this.openDrawer();
    this.setupCloseEvents();
  }

  /**
   * Handles link click events to open the size guide drawer
   *
   * Similar to handleButtonClick, but triggered from links in the
   * product description with the size-guide-opener-link class.
   */
  handleLinkClick() {
    const button = this.sizeGuideButton || document.querySelector('.size-guide-button');
    if (!button) return;

    const sizeGuideData = button.getAttribute('data-size-guide') || '{}';
    const { title, description, image, table, pdf, print_button_text } = JSON.parse(sizeGuideData);

    const tableHTML = this.generateTableHTML(table);
    this.updateDrawerContent(title, description, image, tableHTML, pdf, print_button_text);
    this.openDrawer();
    this.setupCloseEvents();
  }

  /**
   * Converts CSV table data into HTML table markup
   *
   * @param {string} table - CSV-formatted table data with rows separated by newlines
   *                         and cells separated by commas
   * @returns {string} HTML table markup with proper headers and cells
   */
  generateTableHTML(table) {
    let tableHTML = '';
    if (table) {
      const rows = table.split('\n')
        .map(row => row.trim())
        .filter(row => row.length > 0)
        .map(row => row.split(',').map(cell => cell.trim()));

      if (rows.length > 0) {
        tableHTML = '<table>';
        rows.forEach((row, index) => {
          const tag = index === 0 ? 'th' : 'td';
          tableHTML += '<tr>';
          row.forEach(cell => {
            tableHTML += `<${tag}>${cell || ''}</${tag}>`;
          });
          tableHTML += '</tr>';
        });
        tableHTML += '</table>';
      }
    }
    return tableHTML;
  }

  /**
   * Updates the drawer content with the current size guide data
   *
   * This method generates the HTML content for the size guide drawer based on the provided parameters.
   * It creates the structure including header, description, image, table, and PDF link sections.
   *
   * @param {string} title - The title of the size guide to display in the header
   * @param {string} description - The description text for the size guide
   * @param {string} image - URL to the size guide image
   * @param {string} tableHTML - Pre-generated HTML table content
   * @param {string} pdfLink - URL to the downloadable/printable PDF file
   * @param {string} printButtonText - Custom text for the PDF download/print button
   */
  updateDrawerContent(title, description, image, tableHTML, pdfLink, printButtonText) {
    SizeGuide.drawer.innerHTML = `
      <div class="size-guide-drawer__header">
        <h2 class="size-guide-drawer__heading">${title || ''}</h2>
        <button
          class="size-guide-drawer__close"
          type="button"
          aria-label="${window.accessibilityStrings.close}"
        >
          ${SizeGuide.closeIconSVG}
        </button>
      </div>
      <div class="size-guide-drawer__content">
        <p class="size-guide-drawer__description">${description || ''}</p>
        ${image ? `
          <div class="size-guide-drawer__image">
            <img src="${image}" alt="${title || ''}">
          </div>
        ` : ''}
        <div class="size-guide-drawer__table size-guide-table">${tableHTML}</div>
        <div class="size-guide-drawer__pdf">
          ${pdfLink ? `
          <a href="${pdfLink}" target="_blank" rel="noopener noreferrer" class="size-guide-drawer__pdf-link btn primary">
            ${printButtonText || ''}
          </a>
          ` : ''}
      </div>
    `;
  }

  /**
   * Closes any open size guide drawer
   *
   * This method handles the drawer closing process, including:
   * - Removing CSS classes to hide the drawer and overlay
   * - Re-enabling page scrolling
   * - Removing event listeners
   * - Restoring focus to the triggering element
   */
  closeOpenDrawers() {
    if (SizeGuide.drawer.classList.contains('open')) {
      SizeGuide.drawer.classList.remove('open');
      SizeGuide.overlay.classList.remove('active');
      document.documentElement.classList.remove('no-scroll');
      document.removeEventListener('keydown', this.handleEscKey);
      SizeGuide.drawer.removeAttribute('tabindex');
      // Return focus to the element that triggered the drawer opening
      if (this.lastTriggerElement) {
        setTimeout(() => {
          this.lastTriggerElement.focus();
        }, 10);
      } else if (this.openButton) {
        // Fallback to openButton if lastTriggerElement is not set
        setTimeout(() => {
          this.openButton.focus();
        }, 10);
      }
    }
  }

  /**
   * Event handler for Escape key press
   *
   * Closes the drawer when the Escape key is pressed, providing keyboard
   * accessibility for closing the modal.
   *
   * @param {KeyboardEvent} event - The keyboard event object
   */
  handleEscKey(event) {
    if (event.key === 'Escape') {
      this.closeOpenDrawers();
    }
  }

  /**
   * Opens the size guide drawer
   *
   * This method:
   * - Closes any existing open drawers
   * - Activates the overlay
   * - Disables page scrolling
   * - Sets up keyboard event listeners
   * - Makes the drawer focusable for accessibility
   * - Applies CSS transitions for smooth opening animation
   */
  openDrawer() {
    this.closeOpenDrawers();
    SizeGuide.overlay.classList.add('active');
    document.documentElement.classList.add('no-scroll');

    // Add ESC key listener
    document.addEventListener('keydown', this.handleEscKey.bind(this));

    // Ensure the drawer is focusable when opened
    SizeGuide.drawer.setAttribute('tabindex', '0');

    setTimeout(() => {
      SizeGuide.drawer.classList.add('open');
      // Focus the drawer after the drawer is opened
      SizeGuide.drawer.focus();
    }, 10);
  }

  /**
   * Sets up event listeners for closing the drawer
   *
   * Adds click event listeners to the close button and overlay
   * to allow users to close the drawer through various interactions.
   */
  setupCloseEvents() {
    const closeButton = SizeGuide.drawer.querySelector('.size-guide-drawer__close');
    if (closeButton) {
      closeButton.removeEventListener('click', this.closeDrawerHandler);
      this.closeDrawerHandler = () => {
        this.closeOpenDrawers();
      };
      closeButton.addEventListener('click', this.closeDrawerHandler);
    }

    SizeGuide.overlay.removeEventListener('click', this.closeOverlayHandler);
    this.closeOverlayHandler = () => {
      this.closeOpenDrawers();
    };
    SizeGuide.overlay.addEventListener('click', this.closeOverlayHandler);
  }
}

/**
 * Register the custom element with the browser
 * This makes the <size-guide> tag available in HTML
 */
customElements.define('size-guide', SizeGuide);
